import { splitsOf } from '../storage/local';
import { formatTime } from '../render/hud';
import { medalFor } from './medals';
import type { RunResult } from './session';
import type { ObjectiveResult } from './objectives';

/**
 * The compact, pasteable result.
 *
 * The PNG card looks better, but a block of text is what actually spreads:
 * it pastes into any client, renders everywhere, needs no upload, and can be
 * read at a glance in a feed. Wordle's grid is the whole reason that game
 * travelled, and it is three lines of characters.
 *
 * Sector squares use the same colour language as the results screen — purple
 * for a personal best sector, green for close to it, yellow for down on it.
 */
export function buildShareText(
  label: string,
  result: RunResult,
  opts: {
    bestSplits: number[] | null;
    objectives: ObjectiveResult[];
    mode: string;
    weather: string;
  },
): string {
  const medal = medalFor(result.timeMs, result.idealMs);
  const gap = ((result.timeMs - result.idealMs) / 1000).toFixed(2);
  const splits = splitsOf(result.sectorMs);

  const sectorSquares = splits
    .map((split, i) => {
      const best = opts.bestSplits?.[i];
      if (best === undefined) return '🟪';
      if (split <= best * 1.001) return '🟪';
      if (split <= best * 1.02) return '🟩';
      if (split <= best * 1.06) return '🟨';
      return '🟥';
    })
    .join('');

  const objectiveSquares = opts.objectives.map((o) => (o.done ? '✅' : '▫️')).join('');

  const lines = [
    `Daily Karting Hotlap — ${label}`,
    `${medal.emoji} ${formatTime(result.timeMs)}   +${gap}s off ideal`,
    `${sectorSquares} ${splits.map((s) => (s / 1000).toFixed(1)).join(' ')}`,
  ];

  if (opts.objectives.length > 0) lines.push(`${objectiveSquares} objectives`);
  lines.push(`${opts.mode} · ${opts.weather} · ${Math.round(result.maxSpeed * 3.6)} km/h peak`);

  return lines.join('\n');
}
